import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import car from '../../Assets/TreePlanting/Car.png';
import cloud from '../../Assets/TreePlanting/Cloud.png';
import iceburg from '../../Assets/TreePlanting/Iceburg.png';
import { mobileMediaQuery, themeColor } from '../../Theming';
import { useTreePlantedContext } from '../TreeplantingContext';
import CountUp from 'react-countup';

const Container = styled.div`
  width: 100%;
  height: auto;
  display: block;
  text-align: center;
  margin-top: 30px;
  margin-bottom: 40px;

  ${mobileMediaQuery} {
    margin-bottom: 0px;
    height: auto;
  }
`;

const Header = styled.p`
  font-style: normal;
  font-weight: 700;
  font-size: 45px;
  line-height: 54px;
  text-align: center;
  color: ${themeColor('darkestGreen')};
  margin-bottom: 90px;

  ${mobileMediaQuery} {
    font-size: 28px;
    line-height: 34px;
    padding-left: 70px;
    padding-right: 70px;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  text-align: center;
  justify-content: space-evenly;
  align-items: center;

  ${mobileMediaQuery} {
    display: block;
    height: auto;
  }
`;

const InnerContentContainer = styled.div`
  width: 355px;
  height: 300px;
  display: block;
  text-align: center;

  ${mobileMediaQuery} {
    width: 100%;
  }
`;

const IconContainer = styled.div`
  width: 100%;
  height: 140px;
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: center;
`;

const Icon = styled.img`
  max-width: 180px;
  max-height: 140px;

  ${mobileMediaQuery} {
    max-width: 170px;
    max-height: 137px;
  }
`;

const Description = styled.p`
  font-weight: 600;
  font-size: 23px;
  line-height: 25px;
  color: ${themeColor('forestGreen')};
  margin-top: 0px;

  ${mobileMediaQuery} {
    font-size: 20px;
    line-height: 26px;
    padding-left: 90px;
    padding-right: 90px;
  }
`;

const Value = styled.p`
  font-style: normal;
  font-weight: 700;
  font-size: 34px;
  line-height: 41px;
  margin-bottom: 5px;
  margin-top: 20px;
  color: ${themeColor('darkestGreen')};

  ${mobileMediaQuery} {
    font-size: 28px;
    line-height: 34px;
  }
`;

const EquivalentSection: React.FC = () => {
  const { t } = useTranslation();
  const { tonnesOfCo2, squareMeteresOfIceSaved, milesDriven } =
    useTreePlantedContext();

  /*if whole number don't add any decimal places */
  const getDecimal = (value: number) => (Number.isInteger(value) ? 0 : 2);

  return (
    <Container>
      <Header>{t('treePlanting.equivalent.heading')}</Header>
      <ContentContainer>
        <InnerContentContainer>
          <IconContainer>
            <Icon src={cloud} />
          </IconContainer>

          <Value>
            <CountUp
              decimals={getDecimal(tonnesOfCo2 as number)}
              start={0}
              end={tonnesOfCo2 as number}
              duration={1}
            />
          </Value>
          <Description>{t('treePlanting.equivalent.co2')}</Description>
        </InnerContentContainer>
        <InnerContentContainer>
          <IconContainer>
            <Icon src={iceburg} />
          </IconContainer>

          <Value>
            <CountUp
              decimals={getDecimal(squareMeteresOfIceSaved as number)}
              start={0}
              end={squareMeteresOfIceSaved as number}
              duration={1}
            />
          </Value>
          <Description>{t('treePlanting.equivalent.ice')}</Description>
        </InnerContentContainer>
        <InnerContentContainer>
          <IconContainer>
            <Icon src={car} />
          </IconContainer>

          <Value>
            <CountUp
              decimals={getDecimal(milesDriven as number)}
              start={0}
              end={milesDriven as number}
              duration={1}
            />
          </Value>
          <Description>{t('treePlanting.equivalent.miles')}</Description>
        </InnerContentContainer>
      </ContentContainer>
    </Container>
  );
};

export default EquivalentSection;
